import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppLayout from 'layouts/AppLayout';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import { ROUTES } from 'config/navConfig';
import { ROLES } from 'config/roleConfig';
import { useRole } from 'context/RoleContext';
import { DEALS, STAGES, SECTORS, RISK_CONFIG } from '../pipeline/components/pipelineData';
import { MOCK_IC_SUBMISSIONS, MOCK_SPVS } from 'data/mockData';
import { ASSETS as CANONICAL_ASSETS } from '../asset-management/data/assetData';
import { IC_STATES } from 'config/workflowStates';

const STAGE_ORDER = ['sourcing', 'screening', 'diligence', 'ic-review', 'execution', 'asset-mgmt'];

const IC_STATE_CONFIG = {
  [IC_STATES.DRAFT]:                  { label: 'Draft', color: '#64748b', bg: 'rgba(100,116,139,0.1)' },
  [IC_STATES.SUBMITTED]:              { label: 'Submitted', color: '#2563eb', bg: 'rgba(37,99,235,0.1)' },
  [IC_STATES.UNDER_REVIEW]:           { label: 'Under Review', color: '#d97706', bg: 'rgba(217,119,6,0.1)' },
  [IC_STATES.APPROVED]:               { label: 'Approved', color: '#059669', bg: 'rgba(5,150,105,0.1)' },
  [IC_STATES.APPROVED_WITH_CONDITIONS]:{ label: 'Approved with Conditions', color: '#0891b2', bg: 'rgba(8,145,178,0.1)' },
  [IC_STATES.REJECTED]:               { label: 'Rejected', color: '#dc2626', bg: 'rgba(220,38,38,0.1)' },
};

export default function DealRecord() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { role } = useRole();
  const [activeTab, setActiveTab] = useState('overview');

  const deal = DEALS.find((d) => d.id === id);
  const icSubmission = MOCK_IC_SUBMISSIONS.find((s) => s.pipelineDealId === id);
  const linkedSPV = MOCK_SPVS.find((s) => s.dealId === id);
  const linkedAsset = linkedSPV?.assetDataId
    ? CANONICAL_ASSETS.find(a => a.id === linkedSPV.assetDataId)
    : null;
  const sector = SECTORS.find((s) => s.id === deal?.sector);
  const risk = RISK_CONFIG?.[deal?.riskRating];
  const stage = STAGES.find((s) => s.id === deal?.stage);
  const currentStageIdx = STAGE_ORDER.indexOf(deal?.stage);

  const canEditDeal = role === ROLES.INVESTMENT_TEAM || role === ROLES.PLATFORM_ADMIN;
  const canViewIC = role === ROLES.INVESTMENT_TEAM || role === ROLES.IC_MEMBER || role === ROLES.PLATFORM_ADMIN;
  const canViewLegal = role === ROLES.INVESTMENT_TEAM || role === ROLES.LEGAL_COMP_TEAM || role === ROLES.PLATFORM_ADMIN;

  if (!deal) {
    return (
      <AppLayout>
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center">
            <Icon name="AlertCircle" size={40} color="var(--color-muted-foreground)" />
            <p className="font-heading text-lg font-semibold text-foreground mt-4 mb-2">Deal not found</p>
            <p className="font-caption text-sm text-muted-foreground mb-6">The deal record you are looking for does not exist or has been removed.</p>
            <Button variant="default" onClick={() => navigate(ROUTES.pipeline)}>Back to Pipeline</Button>
          </div>
        </div>
      </AppLayout>
    );
  }

  const icCfg = icSubmission ? IC_STATE_CONFIG?.[icSubmission.icState] : null;

  return (
    <AppLayout>
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="sticky top-0 bg-background border-b border-border px-6 md:px-10 lg:px-12 py-4 md:py-5" style={{ zIndex: 'var(--z-nav)' }}>
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 mb-4 text-sm font-caption text-muted-foreground">
            <button onClick={() => navigate(ROUTES.dashboard)} className="hover:text-primary transition-colors">Dashboard</button>
            <Icon name="ChevronRight" size={14} />
            <button onClick={() => navigate(ROUTES.pipeline)} className="hover:text-primary transition-colors">Pipeline</button>
            <Icon name="ChevronRight" size={14} />
            <span className="text-foreground font-medium truncate max-w-xs">{deal.name}</span>
          </nav>

          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div className="flex-1 min-w-0">
              {/* Badges */}
              <div className="flex flex-wrap items-center gap-2 mb-2">
                {stage && (
                  <span
                    className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-caption font-semibold"
                    style={{ color: stage.color, background: stage.bg }}
                  >
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: stage.color }} />
                    {stage.label}
                  </span>
                )}
                {risk && (
                  <span
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-caption font-medium"
                    style={{ color: risk.color, background: risk.bg }}
                  >
                    {deal.riskRating} Risk
                  </span>
                )}
                {sector && (
                  <span
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-caption font-medium"
                    style={{ color: sector.color, background: sector.bg }}
                  >
                    {sector.label}
                  </span>
                )}
              </div>
              <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground mb-1">{deal.name}</h1>
              <div className="flex flex-wrap items-center gap-3 text-sm font-caption text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Icon name="MapPin" size={14} />
                  {deal.region}
                </span>
                <span className="flex items-center gap-1">
                  <Icon name="User" size={14} />
                  {deal.assignedName}
                </span>
                <span className="flex items-center gap-1">
                  <Icon name="Calendar" size={14} />
                  Added {new Date(deal.dateAdded).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                </span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <Button variant="outline" iconName="ArrowLeft" iconPosition="left" iconSize={16} onClick={() => navigate(ROUTES.pipeline)}>
                Back to Pipeline
              </Button>
              {canEditDeal && (
                <Button variant="default" iconName="ClipboardCheck" iconPosition="left" iconSize={16} onClick={() => navigate(ROUTES.icSubmission)}>
                  Submit to IC
                </Button>
              )}
            </div>
          </div>

          {/* Key metrics strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5 pt-5 border-t border-border">
            {[
              { label: 'Target IRR', value: `${deal.targetIRR}%`, icon: 'TrendingUp' },
              { label: 'Ticket Size', value: `£${deal.ticketSize}m`, icon: 'PoundSterling' },
              { label: 'Risk Rating', value: deal.riskRating, icon: 'ShieldAlert' },
              { label: 'Days in Stage', value: `${deal.daysInStage}d`, icon: 'Clock' },
            ].map((m) => (
              <div key={m.label} className="flex items-center gap-3">
                <div className="flex-shrink-0 w-9 h-9 rounded-md flex items-center justify-center" style={{ background: 'rgba(59,76,76,0.08)' }}>
                  <Icon name={m.icon} size={16} color="var(--color-primary)" />
                </div>
                <div>
                  <p className="font-data text-base md:text-lg font-semibold text-foreground">{m.value}</p>
                  <p className="font-caption text-xs text-muted-foreground">{m.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Stage progress bar */}
        <div className="bg-card border-b border-border px-6 md:px-10 lg:px-12 py-3 overflow-x-auto">
          <div className="flex items-center gap-0 min-w-max">
            {STAGES.map((s, idx) => {
              const isDone = idx < currentStageIdx;
              const isCurrent = idx === currentStageIdx;
              return (
                <React.Fragment key={s.id}>
                  <div className="flex flex-col items-center">
                    <div
                      className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{
                        background: isCurrent ? s.bg : isDone ? 'rgba(5,150,105,0.12)' : 'var(--color-muted)',
                        border: `2px solid ${isCurrent ? s.color : isDone ? '#059669' : 'var(--color-border)'}`,
                      }}
                    >
                      {isDone
                        ? <Icon name="Check" size={12} color="#059669" strokeWidth={3} />
                        : <span className="text-xs font-bold" style={{ color: isCurrent ? s.color : 'var(--color-muted-foreground)' }}>{idx + 1}</span>
                      }
                    </div>
                    <span
                      className="font-caption text-xs mt-1 whitespace-nowrap"
                      style={{ color: isCurrent ? s.color : isDone ? '#059669' : 'var(--color-muted-foreground)', fontWeight: isCurrent ? 600 : 400 }}
                    >
                      {s.label}
                    </span>
                  </div>
                  {idx < STAGES.length - 1 && (
                    <div className="w-8 h-0.5 mb-5 mx-1 flex-shrink-0" style={{ background: idx < currentStageIdx ? '#059669' : 'var(--color-border)' }} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Tab nav */}
        <div className="bg-card border-b border-border px-6 md:px-10 lg:px-12">
          <div className="flex gap-1 overflow-x-auto">
            {[
              { id: 'overview', label: 'Overview', icon: 'FileText' },
              ...(canViewIC ? [{ id: 'ic', label: 'IC Submission', icon: 'ClipboardCheck' }] : []),
              ...(canViewLegal ? [{ id: 'legal', label: 'Legal & Docs', icon: 'Scale' }] : []),
              { id: 'timeline', label: 'Timeline', icon: 'GitBranch' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className="flex items-center gap-2 px-4 py-3 text-sm font-caption font-medium border-b-2 transition-colors whitespace-nowrap"
                style={{
                  borderColor: activeTab === tab.id ? 'var(--color-primary)' : 'transparent',
                  color: activeTab === tab.id ? 'var(--color-primary)' : 'var(--color-muted-foreground)',
                }}
              >
                <Icon name={tab.icon} size={14} color={activeTab === tab.id ? 'var(--color-primary)' : 'var(--color-muted-foreground)'} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab content */}
        <div className="px-6 md:px-10 lg:px-12 py-6">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Description */}
              <div className="lg:col-span-2 space-y-5">
                <div className="card p-5">
                  <h3 className="font-heading font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Icon name="FileText" size={16} color="var(--color-primary)" />
                    Deal Description
                  </h3>
                  <p className="font-caption text-sm text-foreground leading-relaxed">{deal.description}</p>
                </div>

                {icSubmission && icCfg && canViewIC && (
                  <div
                    className="card p-5"
                    style={{ borderColor: icCfg.color + '40' }}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-heading font-semibold text-foreground flex items-center gap-2">
                        <Icon name="ClipboardCheck" size={16} color="var(--color-primary)" />
                        IC Submission
                      </h3>
                      <span
                        className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-caption font-semibold"
                        style={{ background: icCfg.bg, color: icCfg.color }}
                      >
                        {icCfg.label}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div><p className="font-caption text-xs text-muted-foreground">Submitted by</p><p className="font-caption font-medium text-foreground">{icSubmission.submittedBy}</p></div>
                      <div><p className="font-caption text-xs text-muted-foreground">IC Date</p><p className="font-caption font-medium text-foreground">{icSubmission.icDate}</p></div>
                      <div><p className="font-caption text-xs text-muted-foreground">Deal Size</p><p className="font-caption font-medium text-foreground">{icSubmission.dealSize}</p></div>
                      <div><p className="font-caption text-xs text-muted-foreground">Target IRR</p><p className="font-caption font-medium text-foreground">{icSubmission.targetIRR}</p></div>
                    </div>
                    {icSubmission.conditions && (
                      <div className="mt-3 pt-3 border-t border-border">
                        <p className="font-caption text-xs text-muted-foreground mb-1">Conditions</p>
                        <p className="font-caption text-xs text-foreground leading-relaxed">{icSubmission.conditions}</p>
                      </div>
                    )}
                    {canViewIC && (
                      <Button variant="outline" size="sm" iconName="ArrowRight" iconPosition="right" iconSize={13} className="mt-3" onClick={() => navigate(ROUTES.icSubmission)}>
                        View Full IC Submission
                      </Button>
                    )}
                  </div>
                )}

                {!icSubmission && canEditDeal && (
                  <div className="card p-5 border-dashed">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Icon name="ClipboardCheck" size={18} color="var(--color-primary)" />
                      </div>
                      <div className="flex-1">
                        <p className="font-caption font-semibold text-foreground text-sm">No IC submission yet</p>
                        <p className="font-caption text-xs text-muted-foreground mt-0.5">Submit this deal to the Investment Committee when due diligence is complete.</p>
                      </div>
                      <Button variant="default" size="sm" iconName="Plus" iconPosition="left" iconSize={13} onClick={() => navigate(ROUTES.icSubmission)}>
                        Submit to IC
                      </Button>
                    </div>
                  </div>
                )}

                {linkedSPV && (
                  <div className="card p-5" style={{ borderColor: 'rgba(5,150,105,0.3)' }}>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-heading font-semibold text-foreground flex items-center gap-2">
                        <Icon name="Building2" size={16} color="#059669" />
                        Linked SPV
                      </h3>
                      <span
                        className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-caption font-semibold"
                        style={{
                          color: linkedSPV.status === 'open' ? '#059669' : '#d97706',
                          background: linkedSPV.status === 'open' ? 'rgba(5,150,105,0.1)' : 'rgba(217,119,6,0.1)',
                        }}
                      >
                        <span
                          className="w-1.5 h-1.5 rounded-full"
                          style={{ background: linkedSPV.status === 'open' ? '#059669' : '#d97706' }}
                        />
                        {linkedSPV.status === 'open' ? 'Open' : 'In Formation'}
                      </span>
                    </div>
                    <p className="font-caption text-sm font-medium text-foreground mb-3">{linkedSPV.name}</p>
                    <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                      <div><p className="font-caption text-xs text-muted-foreground">Target Size</p><p className="font-caption font-medium text-foreground">£{(linkedSPV.targetSize / 1_000_000).toFixed(0)}m</p></div>
                      <div><p className="font-caption text-xs text-muted-foreground">Target IRR</p><p className="font-caption font-medium text-foreground">{linkedSPV.targetIRR}</p></div>
                      <div><p className="font-caption text-xs text-muted-foreground">Vehicle</p><p className="font-caption font-medium text-foreground">{linkedSPV.vehicleType}</p></div>
                      <div><p className="font-caption text-xs text-muted-foreground">Closing Date</p><p className="font-caption font-medium text-foreground">{linkedSPV.closingDate}</p></div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="ArrowRight"
                      iconPosition="right"
                      iconSize={13}
                      onClick={() => navigate(ROUTES.spvDashboard, { state: { spvId: linkedSPV.id } })}
                    >
                      Open SPV Dashboard
                    </Button>
                  </div>
                )}

                {linkedAsset && (
                  <div className="card p-5 mt-4" style={{ borderLeft: '3px solid #0891b2' }}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Icon name="Building2" size={16} color="#0891b2" />
                        <span className="font-caption text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          Underlying Asset
                        </span>
                      </div>
                      <span
                        className="font-caption text-xs font-semibold px-2 py-0.5"
                        style={{ background: 'rgba(8,145,178,0.1)', color: '#0891b2', borderRadius: '3px' }}
                      >
                        {linkedAsset.stage}
                      </span>
                    </div>
                    <p className="font-heading font-semibold text-foreground text-base mb-1">{linkedAsset.name}</p>
                    <p className="font-caption text-xs text-muted-foreground mb-3">{linkedAsset.sector} · {linkedAsset.country}</p>
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      {[
                        { label: 'Target IRR', value: `${linkedAsset.irr}%` },
                        { label: 'Investment Value', value: `£${linkedAsset.totalInvestmentValue}m` },
                        { label: 'ESG Score', value: `${linkedAsset.esgScore}/100` },
                        { label: 'Risk Score', value: `${linkedAsset.riskScore}/5` },
                      ].map(({ label, value }) => (
                        <div key={label} className="rounded-lg p-3" style={{ background: 'var(--color-muted)' }}>
                          <p className="font-caption text-xs text-muted-foreground">{label}</p>
                          <p className="font-caption text-sm font-semibold text-foreground">{value}</p>
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={() => navigate(`${ROUTES.portfolioAssets}?id=${linkedAsset.id}`)}
                      className="w-full font-caption text-xs font-semibold py-2 rounded-lg transition-colors"
                      style={{ background: 'rgba(8,145,178,0.1)', color: '#0891b2' }}
                    >
                      View Asset Detail →
                    </button>
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <div className="space-y-5">
                <div className="card p-5">
                  <h3 className="font-caption font-semibold text-foreground mb-3 text-sm">Deal Details</h3>
                  <dl className="space-y-2.5">
                    {[
                      { label: 'Sector', value: sector?.label || deal.sector },
                      { label: 'Region', value: deal.region },
                      { label: 'Target IRR', value: `${deal.targetIRR}%` },
                      { label: 'Ticket Size', value: `£${deal.ticketSize}m` },
                      { label: 'Risk Rating', value: deal.riskRating },
                      { label: 'Lead', value: deal.assignedName },
                      { label: 'Current Stage', value: stage?.label || deal.stage },
                      { label: 'Days in Stage', value: `${deal.daysInStage} days` },
                    ].map(({ label, value }) => (
                      <div key={label} className="flex items-start justify-between gap-2">
                        <dt className="font-caption text-xs text-muted-foreground flex-shrink-0">{label}</dt>
                        <dd className="font-caption text-xs font-medium text-foreground text-right">{value}</dd>
                      </div>
                    ))}
                  </dl>
                </div>

                <div className="card p-5">
                  <h3 className="font-caption font-semibold text-foreground mb-3 text-sm">Quick Actions</h3>
                  <div className="space-y-2">
                    <Button variant="outline" fullWidth iconName="FolderLock" iconPosition="left" iconSize={14} onClick={() => navigate(ROUTES.dataRoom)}>
                      Document Library
                    </Button>
                    {canEditDeal && (
                      <Button variant="outline" fullWidth iconName="Layers" iconPosition="left" iconSize={14} onClick={() => navigate(ROUTES.structuringHub)}>
                        Structuring Hub
                      </Button>
                    )}
                    <Button variant="outline" fullWidth iconName="Activity" iconPosition="left" iconSize={14} onClick={() => navigate(ROUTES.activityFeed)}>
                      Activity Feed
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'ic' && canViewIC && (
            <div className="card p-6 max-w-2xl">
              <h3 className="font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="ClipboardCheck" size={16} color="var(--color-primary)" />
                IC Submission Status
              </h3>
              {icSubmission ? (
                <>
                  <div className="flex items-center gap-3 mb-5">
                    <span
                      className="inline-flex items-center px-3 py-1 rounded text-sm font-caption font-semibold"
                      style={{ background: icCfg?.bg, color: icCfg?.color }}
                    >
                      {icCfg?.label}
                    </span>
                    <span className="font-caption text-sm text-muted-foreground">Submitted {icSubmission.submittedDate}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-5">
                    {[
                      { label: 'Submission ID', value: icSubmission.id },
                      { label: 'Lead Analyst', value: icSubmission.leadAnalyst },
                      { label: 'Submitted By', value: icSubmission.submittedBy },
                      { label: 'IC Date', value: icSubmission.icDate },
                      { label: 'Deal Size', value: icSubmission.dealSize },
                      { label: 'Target IRR', value: icSubmission.targetIRR },
                    ].map(({ label, value }) => (
                      <div key={label}>
                        <p className="font-caption text-xs text-muted-foreground">{label}</p>
                        <p className="font-caption text-sm font-medium text-foreground">{value}</p>
                      </div>
                    ))}
                  </div>
                  {icSubmission.conditions && (
                    <div className="p-4 rounded-lg mb-4" style={{ background: 'rgba(8,145,178,0.07)', border: '1px solid rgba(8,145,178,0.2)' }}>
                      <p className="font-caption text-xs font-semibold text-foreground mb-1">Approval Conditions</p>
                      <p className="font-caption text-sm text-foreground leading-relaxed">{icSubmission.conditions}</p>
                    </div>
                  )}
                  <Button variant="default" iconName="ArrowRight" iconPosition="right" iconSize={14} onClick={() => navigate(ROUTES.icSubmission)}>
                    Open Full IC Submission
                  </Button>
                </>
              ) : (
                <div className="text-center py-8">
                  <Icon name="ClipboardCheck" size={32} color="var(--color-muted-foreground)" />
                  <p className="font-caption text-sm text-muted-foreground mt-3 mb-4">No IC submission for this deal yet.</p>
                  {canEditDeal && (
                    <Button variant="default" iconName="Plus" iconPosition="left" iconSize={14} onClick={() => navigate(ROUTES.icSubmission)}>
                      Create IC Submission
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'legal' && canViewLegal && (
            <div className="card p-6 max-w-2xl">
              <h3 className="font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="Scale" size={16} color="var(--color-primary)" />
                Legal &amp; Documentation
              </h3>
              <p className="font-caption text-sm text-muted-foreground mb-5">Deal documentation, legal agreements, and compliance materials are managed through the Document Library.</p>
              <div className="space-y-3">
                <Button variant="outline" fullWidth iconName="FolderLock" iconPosition="left" iconSize={14} onClick={() => navigate(ROUTES.dataRoom)}>
                  Open Document Library
                </Button>
                <Button variant="outline" fullWidth iconName="ClipboardList" iconPosition="left" iconSize={14} onClick={() => navigate(ROUTES.auditTrail)}>
                  View Audit Trail
                </Button>
              </div>
            </div>
          )}

          {activeTab === 'timeline' && (
            <div className="card p-6 max-w-2xl">
              <h3 className="font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="GitBranch" size={16} color="var(--color-primary)" />
                Deal Timeline
              </h3>
              <div className="space-y-4">
                {[
                  { label: 'Deal Added to Pipeline', date: new Date(deal.dateAdded).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }), done: true, icon: 'Plus' },
                  { label: 'Initial Screening', date: deal.stage !== 'sourcing' ? '—' : 'Pending', done: STAGE_ORDER.indexOf(deal.stage) > 0, icon: 'Search' },
                  { label: 'Due Diligence', date: STAGE_ORDER.indexOf(deal.stage) >= 2 ? '—' : 'Pending', done: STAGE_ORDER.indexOf(deal.stage) > 2, icon: 'FileSearch' },
                  { label: 'IC Submission', date: icSubmission ? icSubmission.submittedDate : 'Pending', done: !!icSubmission, icon: 'ClipboardCheck' },
                  { label: 'IC Approval', date: icSubmission?.icState === IC_STATES.APPROVED || icSubmission?.icState === IC_STATES.APPROVED_WITH_CONDITIONS ? icSubmission.icDate : 'Pending', done: icSubmission?.icState === IC_STATES.APPROVED || icSubmission?.icState === IC_STATES.APPROVED_WITH_CONDITIONS, icon: 'CheckCircle' },
                  { label: 'Execution / Close', date: deal.stage === 'execution' || deal.stage === 'asset-mgmt' ? '—' : 'Pending', done: deal.stage === 'execution' || deal.stage === 'asset-mgmt', icon: 'Zap' },
                ].map((step, idx) => (
                  <div key={idx} className="flex items-start gap-4">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ background: step.done ? 'rgba(5,150,105,0.12)' : 'var(--color-muted)', border: `2px solid ${step.done ? '#059669' : 'var(--color-border)'}` }}
                    >
                      <Icon name={step.done ? 'Check' : step.icon} size={14} color={step.done ? '#059669' : 'var(--color-muted-foreground)'} />
                    </div>
                    <div className="flex-1 min-w-0 pt-1">
                      <p className="font-caption text-sm font-medium text-foreground">{step.label}</p>
                      <p className="font-caption text-xs text-muted-foreground">{step.date}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
